import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';
import { IAPIReturnObject } from '../interfaces/APIReturnObject';
import { ICaseData } from 'interfaces/ICase';
import { IColumnHeader } from 'interfaces/IFileInterfaces';
import { IDailyReportInfo } from 'interfaces/dailyReport';
import { IProduct } from './bitedb/products/products';
import { IUpload } from 'api/bitedb/files/file';

interface ISendToBroker {
  recipientType: string;
  recipientId: string;
  recipient: string;
  from: string;
  recipientEmail: string;
  headers: IColumnHeader[];
  format: 'csv' | 'xlsx' | 'pdf' | 'api';
  selectedLines: IProduct[];
  selectedDocuments: IUpload[];
  includeBomBreakdown: boolean;
  bomBreakdown?: Record<string, Array<IProduct & { bom_depth: number }>>;
  includeEvidence: boolean;
  acknowledgeWarnings: boolean;
  notes: string;
}

interface IUserBasics {
  firstName: string;
  lastName: string;
  email: string;
}

interface IServiceFormData {
  team_admin_id: string;
  admin_phone: string;
  admin_name: string;
  service_acct_email: string;
  selected_team: string;
}

interface IAccountRequestFormData {
  firstName: string;
  lastName: string;
  email: string;
  requestorEmail: string;
}

interface IAccountUpdateRequestFormData {
  username: string;
  existingUser: IUserBasics;
  updatedUser: IUserBasics;
  requestorEmail: string;
}

interface IAccountDeleteRequestFormData {
  username: string;
  requestorEmail: string;
}

interface ITeamDeleteRequestFormData {
  name: string;
  id: string;
  admin: string;
}

interface IRiskAnalysisData {
  requestorEmail: string;
  caseData: ICaseData;
}

interface ICaseAssigneeData {
  requestorEmail: string;
  caseData: ICaseData;
  assigneeEmails: string[];
}

interface IServiceAccountRequestData {
  requestorEmail: string;
  formData: IServiceFormData;
}

interface IDailyReportRequest {
  groupId?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  teamName?: string;
  date?: string;
  requestorEmail?: string;
}

interface IDailyReportUpdate {
  requestorEmail: string;
  updateInfo: IDailyReportInfo;
}

interface ITicketCreated {
  ticketID?: string;
  ticketName?: string;
  email?: string;
  type?: string;
  groupName?: string;
}

interface ISendTariffResultPdfRequest {
  recipients: string[];
  fileName: string;
  pdfBase64: string;
  docType?: string;
  ticketId?: string;
  ticketName?: string;
  senderName?: string;
  senderEmail?: string;
}

export const EmailAPI = {
  serviceAccountRequest: async (
    formValues: IServiceAccountRequestData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/request-service-account', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  inviteUserToTeam: async (
    formValues: any,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/invite-user', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  workflowCaseNotification: async (
    formValues: ICaseAssigneeData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/case-notification', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  adminRequest: async (
    formValues: IAccountRequestFormData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/admin-request', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  accountRequest: async (
    formValues: IAccountRequestFormData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/account-request', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  accountUpdateRequest: async (
    formValues: IAccountUpdateRequestFormData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/update-user', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  accountDeleteRequest: async (
    formValues: IAccountDeleteRequestFormData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/delete-user', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  teamDeleteRequest: async (
    formValues: ITeamDeleteRequestFormData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/delete-team', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  riskAnalysis: async (
    formValues: IRiskAnalysisData,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/risk-analysis', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err: unknown) => {
        if (err instanceof Error) {
          return {
            success: false,
            result: err.message,
          };
        }

        if (typeof err === 'string') {
          return {
            success: false,
            result: err,
          };
        }

        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  dailyReportRequest: async (
    formValues: IDailyReportRequest,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/daily-report-request', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  dailyReportUpdate: async (
    formValues: IDailyReportUpdate,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/daily-report-update', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  ticketCreated: async (
    formValues: ITicketCreated,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/ticket-created', JSON.stringify(formValues))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  sendToBroker: async (
    data: ISendToBroker,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/send-to-broker', JSON.stringify(data))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  sendTariffResultPdf: async (
    data: ISendTariffResultPdfRequest,
  ): Promise<IAPIReturnObject | undefined> =>
    (await initAxios())
      ?.post('/email/send-tariff-result-pdf', JSON.stringify(data))
      .then(() => {
        return {
          success: true,
          result: null,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
};

export default EmailAPI;
